import { animated, config, Transition } from "@react-spring/web";
import { useSyncExternalStore, useCallback } from "react";
import { BehaviorSubject } from "rxjs";

import Message from "./message";
import * as styles from "./styles.css";

import type { Notified, Notification } from "./types";
import type { FC, ReactNode } from "react";

const AnimatedMessage = animated(Message);

let notifyKey = 0;
const store = new BehaviorSubject<Notified[]>([]);

const notify = ({ type, message, placement, duration, closable }: Notification): Notified => {
  const id = `${notifyKey++}`;
  const _duration = duration ?? 3000;
  const _placement = placement ?? "bottomRight";
  const onclose: (() => void)[] = [];

  const close = () => {
    store.next(store.getValue().filter((item) => item.id !== id));
    onclose.forEach((listener) => listener());
  };

  const clearId = setTimeout(() => {
    if (_duration === Infinity) return;

    close();
  }, _duration);

  const n: Notified = {
    id,
    type,
    message,
    duration: _duration,
    placement: _placement,
    close: () => {
      clearTimeout(clearId);
      close();
    },
    onclose: (listener) => onclose.push(listener),
    closable: closable ?? true,
  };
  store.next([...store.getValue(), n]);

  return n;
};

type Props = {
  children?: ReactNode;
};
const NotificationProvider: FC<Props> = ({ children }) => {
  const getKeys = useCallback((item: Notified) => item.id, []);
  const notifications = useSyncExternalStore(
    (onStoreChange) => {
      const subscription = store.subscribe(onStoreChange);

      return () => subscription.unsubscribe();
    },
    () => store.getValue(),
    () => store.getValue(),
  );

  return (
    <>
      {children}

      <div className={styles.rightBottom}>
        <Transition items={notifications} from={{ opacity: 0 }} enter={{ opacity: 1 }} leave={{ opacity: 0 }} config={config.stiff} keys={getKeys}>
          {(style, item) => (
            <AnimatedMessage
              type={item.type}
              key={item.id}
              style={{
                opacity: style.opacity,
                transform: style.opacity.to((y: number) => `translate(${(1 - y) * 100}px`),
              }}
              onClose={item.close}
            >
              {item.message}
            </AnimatedMessage>
          )}
        </Transition>
      </div>
    </>
  );
};

export { notify, NotificationProvider };
export type { Notification };
